/**
 * Give each art script its own copy of the names it forgets to declare.
 *
 * The scripts are full of assignments to undeclared identifiers — getHashData,
 * printMetadata, palettes, whole helper libraries. In a function body those
 * become properties of window, shared by every instance of that script on the
 * page. Nine sketches constructed together all overwrite them, and eight then
 * render from a tenth cell's state.
 *
 * The grid worked around that by creating sketches one at a time, which fixed
 * the collision and ruined the effect: BigBang's hundred cells are meant to
 * appear together and animate in step, not trickle in over two seconds.
 *
 * So the names are declared instead. A `var` preamble inside the same function
 * body turns each one into a local of that sketch, which is what the scripts
 * assume they are. This runs once when the sketch is built and costs nothing
 * afterwards — the reason not to do it with `with` and a Proxy, which would put
 * a trap on every identifier read inside a draw loop running sixty times a
 * second across a hundred canvases.
 *
 * The scripts are immutable on chain, so this is the only place the problem can
 * be corrected.
 */

// Never shadow these: the scripts read them expecting the real thing.
const PROTECTED = new Set([
  'p', 'p5', 'window', 'document', 'console', 'navigator', 'location', 'history',
  'localStorage', 'sessionStorage', 'fetch', 'performance', 'screen',
  'Math', 'JSON', 'Date', 'Array', 'Object', 'String', 'Number', 'Boolean',
  'RegExp', 'Error', 'Promise', 'Symbol', 'Map', 'Set', 'WeakMap', 'WeakSet',
  'Proxy', 'Reflect', 'BigInt', 'Function', 'ArrayBuffer', 'DataView',
  'Uint8Array', 'Uint8ClampedArray', 'Uint16Array', 'Uint32Array',
  'Int8Array', 'Int16Array', 'Int32Array', 'Float32Array', 'Float64Array',
  'THREE', 'undefined', 'NaN', 'Infinity', 'globalThis', 'self',
  'setTimeout', 'setInterval', 'clearTimeout', 'clearInterval',
  'requestAnimationFrame', 'cancelAnimationFrame', 'alert', 'Image', 'Audio',
  'Blob', 'URL', 'FileReader', 'CustomEvent', 'Event',
]);

// Reserved words can appear before "=" in ways that are not assignments.
const RESERVED = new Set([
  'if', 'else', 'for', 'while', 'do', 'switch', 'case', 'default', 'break',
  'continue', 'return', 'function', 'class', 'new', 'delete', 'typeof',
  'instanceof', 'in', 'of', 'let', 'const', 'var', 'this', 'super', 'try',
  'catch', 'finally', 'throw', 'void', 'yield', 'await', 'async', 'true',
  'false', 'null',
]);

// A bare `name =` that begins a statement. Anything reached through a dot or a
// bracket is a property, not a binding, and is left alone.
const ASSIGNED = /(?:^|[;{}\n])\s*([A-Za-z_$][\w$]*)\s*=(?!=|>)/g;

/**
 * Names this script declares somewhere. Deliberately generous: a name that
 * looks declared is left alone, which preserves today's behaviour rather than
 * risking a shadow that changes what the art draws.
 */
function declaredNames(script) {
  const names = new Set();
  let match;

  const bindings = /\b(?:let|const|var)\s+([^;\n]+)/g;
  while ((match = bindings.exec(script))) {
    // Split a list like "a, b = 1, c" into its leading identifiers.
    for (const part of match[1].split(',')) {
      const name = /^\s*([A-Za-z_$][\w$]*)/.exec(part);
      if (name) names.add(name[1]);
    }
  }

  const named = /\b(?:function|class)\s+([A-Za-z_$][\w$]*)/g;
  while ((match = named.exec(script))) names.add(match[1]);

  return names;
}

/**
 * The identifiers this script assigns without ever declaring.
 */
export function implicitGlobals(script) {
  if (!script || typeof script !== 'string') return [];
  const declared = declaredNames(script);
  const found = new Set();

  let match;
  ASSIGNED.lastIndex = 0;
  while ((match = ASSIGNED.exec(script))) {
    const name = match[1];
    if (declared.has(name) || PROTECTED.has(name) || RESERVED.has(name)) continue;
    found.add(name);
    // The match consumed the character before the name; step back so an
    // assignment immediately following another is not skipped.
    ASSIGNED.lastIndex = match.index + match[0].length - 1;
  }

  return [...found];
}

// Every cell of a project builds the same script, so the work is done once.
const cache = new Map();

/**
 * The script with those names declared as locals of its own function body.
 *
 * Which names are already declared is decided by the parser rather than by
 * reading the source: these scripts declare across several lines, with comments
 * in between, and a scanner that guesses wrong produces "Identifier 'state' has
 * already been declared" and no art at all. So the preamble is offered to the
 * parser, and any name it objects to is dropped and the rest tried again.
 *
 * If it cannot be made to parse for some other reason, the original script is
 * returned untouched: the collision is a visual defect, a broken script is a
 * blank cell.
 */
export function scopeScript(script) {
  if (!script || typeof script !== 'string') return script;
  if (cache.has(script)) return cache.get(script);

  const names = implicitGlobals(script);
  let result = script;

  if (names.length) {
    const remaining = new Set(names);
    // One removal per attempt, plus a little slack.
    for (let attempt = 0; attempt <= names.length + 1; attempt++) {
      if (!remaining.size) { result = script; break; }
      const candidate = `var ${[...remaining].join(', ')};\n${script}`;
      try {
        // eslint-disable-next-line no-new-func
        new Function('p', candidate);
        result = candidate;
        break;
      } catch (error) {
        const clash = /Identifier '([^']+)' has already been declared/.exec(error.message);
        if (clash && remaining.has(clash[1])) {
          remaining.delete(clash[1]);
          continue;
        }
        // Not something dropping a name will fix.
        result = script;
        break;
      }
    }
  }

  cache.set(script, result);
  return result;
}

export default scopeScript;
